import React, { useEffect, useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Body, BodyBold } from '@components/dashboard/Typography';
import Colors from '@components/shared/Colors';
import { ProductBuyOptionInfo } from '@contracts';
import IconArrow from '@images/icon-arrow.svg';
import IconTick from '@images/IconTick';
import { getSanitizedKey } from '@utils/helpers';

interface Props {
  info: ProductBuyOptionInfo[];
  openDefault: boolean;
}

const ProductBuyOptionsInfo: React.FC<Props> = ({ info, openDefault }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(openDefault);
  }, [openDefault]);

  const renderItem = (item: ProductBuyOptionInfo, index: number) => {
    switch (item.type) {
      case 'space':
        return <div key={`space-${index}`}></div>;
      case 'bold': {
        const sanitizedKey = item.text ? getSanitizedKey(item.text) : index;
        return <BodyBold key={sanitizedKey}>{item.text}</BodyBold>;
      }
      case 'listWithTicks':
        return (
          <List key={`listWithTicks-${index}`}>
            {item.items?.map((listItem, listItemIndex) => {
              const sanitizedKey = listItem ? getSanitizedKey(listItem) : listItemIndex;
              return (
                <li key={sanitizedKey}>
                  <IconTick />
                  <Body>{listItem}</Body>
                </li>
              );
            })}
          </List>
        );
      default:
        return <Body key={`text-${item.text}-${index}`}>{item.text}</Body>;
    }
  };

  const renderOptionInfo = () => {
    // If isOpen is false, render only the first item (preview mode)
    if (!isOpen) {
      return info.slice(0, 1).map(renderItem);
    }

    // If isOpen is true, render all items
    return info.map(renderItem);
  };

  return (
    <Container>
      {renderOptionInfo()}
      <ToggleLink onClick={() => setIsOpen(!isOpen)} id="product-buy-options-info-toggle">
        <span>{isOpen ? t('components.close') : t('components.more-info')}</span>
        <IconArrowStyled />
      </ToggleLink>
    </Container>
  );
};

export default ProductBuyOptionsInfo;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 8px;
  align-self: stretch;
`;

const List = styled.ul`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
  list-style: none;
  margin: 0;
  padding: 0;

  & li {
    display: flex;
    align-items: center;
    gap: 5px;

    & svg {
      width: 8px;
      height: 8px;
    }

    & p {
      font-size: 12px;
      line-height: 140%;
    }
  }
`;

const ToggleLink = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 0;
  padding: 0;
  font-family: BrownStd;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  color: ${Colors.navyBlue};
  margin-top: 10px;
`;

const IconArrowStyled = styled(IconArrow)`
  width: 10px;
  height: 6px;
  transform: rotate(-90deg);
`;
